import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

export type IncidenceGroupBy = 'type' | 'van';

export interface IncidenceRow {
  /** Van id, or the reason text, so rows stay stable between renders. */
  key: string;
  label: string;
  count: number;
  /** Share of all incidents, rounded, no decimals. */
  pct: number;
}

export interface IncidenceRanking {
  total: number;
  top: IncidenceRow[];
  /** Groups beyond the top, and the incidents they hold between them. */
  restGroups: number;
  restCount: number;
}

/** How many groups the card lists. */
export const INCIDENCE_TOP = 5;

/**
 * Incidents of `stops` grouped by reason ("type") or by van, biggest first
 * (ties by name), limited to the top 5 plus a summary of the rest. Stops
 * without a reason are grouped under `noReasonLabel`.
 */
export const buildIncidenceRanking = (
  stops: TiendaPerfectaVisitHistoryStop[],
  groupBy: IncidenceGroupBy,
  noReasonLabel: string
): IncidenceRanking => {
  const groups = new Map<string, { label: string; count: number }>();
  let total = 0;

  stops.forEach((stop) => {
    if (stop.outcome !== 'incident') return;
    total += 1;
    const reason = stop.incident?.reason?.trim() || noReasonLabel;
    const key = groupBy === 'van' ? stop.van.id : reason;
    const label = groupBy === 'van' ? stop.van.name : reason;
    const group = groups.get(key) ?? { label, count: 0 };
    group.count += 1;
    groups.set(key, group);
  });

  const sorted = [...groups.entries()]
    .map(([key, group]) => ({ key, ...group }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const rest = sorted.slice(INCIDENCE_TOP);

  return {
    total,
    top: sorted.slice(0, INCIDENCE_TOP).map((row) => ({
      ...row,
      pct: Math.round((row.count / total) * 100),
    })),
    restGroups: rest.length,
    restCount: rest.reduce((sum, row) => sum + row.count, 0),
  };
};
